// release-g3-rerequest.ts
//
// B2-1 (design H14 bản 2, 2026-09-29): a G3 request/approval is bound to
// `<candidate>:<release manifest hash>`. When what is on disk no longer
// matches, a fresh G3 is requested BEFORE any side effect. Split out of
// supervisor-release-flow-resume.ts (line budget, review L6).
//   - pending or approved, same candidate, other manifest, a push of it to the
//     CURRENT destination on record ⇒ re-request: the person must not approve
//     an id for content that will not run (L8);
//   - pending or approved, OTHER candidate — or same candidate whose release
//     destination (remote/target_branch) has no push yet ⇒ withdrawn by
//     readG3WithdrawingOtherCandidate (Codex review CXR-03, rereview CXR-R2):
//     it goes back through review → push → CI before any G3 names it.
// A concurrent writer (an `approve` landing between read and write) makes
// requestGate throw GateRevisionConflictError; that is reported as "waiting"
// on whatever request is current rather than escaping `run` (L9).
import { randomUUID } from "node:crypto";
import {
  deriveGateRequestId,
  type GateFile,
  GateRevisionConflictError,
  invalidateGateOnShaChange,
  readGateFile,
  requestGate,
} from "./gate-registry.js";
import type { MissionStore } from "./mission-store.js";
import { findRecordedPush, type PushDestination } from "./recorded-draft-mr-push.js";
import {
  computeReleaseManifestHash,
  g3ArtifactHash,
  type ReleaseConfig,
} from "./release-manifest-hash.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

export const RELEASE_MANIFEST_CHANGED_REASON =
  "release manifest (deploy/verify recipe hoặc tham số config.release) đã đổi kể từ lần request/" +
  "duyệt G3 trước — approval cũ không áp cho nội dung mới, cần duyệt G3 mới trước khi deploy.";

/** A concurrent gates.json write (an `approve`?) — act on nothing, the next `run` re-reads. */
export const G3_CONCURRENT_WRITE_REASON =
  "gates.json vừa được ghi đồng thời (approve?) — chạy lại `run` để xét lại G3.";

export async function reRequestG3IfStale(input: {
  controlRoot: string;
  g3: GateFile["g3"];
  candidateSha: string;
  releaseManifestHash: string;
}): Promise<{ requestId: string; reason: string } | undefined> {
  const { g3 } = input;
  const current = g3ArtifactHash(input.candidateSha, input.releaseManifestHash);
  if (g3.artifact_hash === undefined || g3.artifact_hash === current) return undefined;
  // Another candidate — or an unpushed destination — was withdrawn before this
  // point (readG3WithdrawingOtherCandidate).
  if (!g3.artifact_hash.startsWith(`${input.candidateSha}:`)) return undefined;

  const reason = RELEASE_MANIFEST_CHANGED_REASON;
  const requestId = deriveGateRequestId("g3", current);
  try {
    await requestGate(input.controlRoot, "g3", requestId, current, () => new Date(), reason);
  } catch (err) {
    if (!(err instanceof GateRevisionConflictError)) throw err;
    const now = (await readGateFile(input.controlRoot)).g3;
    return {
      requestId: now.request_id ?? "",
      reason: G3_CONCURRENT_WRITE_REASON,
    };
  }
  return { requestId, reason };
}

/**
 * CXR-03 (Codex review 2026-10-01): G3 pending/approved for candidate A while
 * the mission HEAD is now B (a redirect, a follow-up ticket). Asking G3 for B
 * straight away skipped B's review receipt, push and CI — and, approved, ended
 * `done_without_deploy` for an MR that never received B. The old request or
 * approval is invalidated instead, so the caller takes the not_requested path
 * (review → push → CI → G3). A concurrent writer (`approve` landing between
 * read and write) just leaves the current state for the next `run`.
 */
export async function readG3WithdrawingOtherCandidate(input: {
  store: Pick<MissionStore, "commit" | "state">;
  controlRoot: string;
  release: ReleaseConfig;
  /** Where this release pushes now (CXR-R2) — remote + target branch from config.release. */
  destination: PushDestination;
  readCandidateSha: () => Promise<string>;
}): Promise<{ g3: GateFile["g3"]; withdrawalNote?: string; concurrentWrite?: true }> {
  const g3 = (await readGateFile(input.controlRoot)).g3;
  if ((g3.status !== "pending" && g3.status !== "approved") || g3.artifact_hash === undefined) {
    return { g3 };
  }
  const candidateSha = await input.readCandidateSha();
  const current = g3ArtifactHash(
    candidateSha,
    computeReleaseManifestHash(candidateSha, input.release),
  );
  const sameCandidate = g3.artifact_hash.startsWith(`${candidateSha}:`);
  // CXR-R2: the manifest also holds remote/target_branch — a changed manifest
  // keeps this G3 (for a direct re-request) only once the candidate reached
  // the CURRENT destination; a recipe-only edit has that push on record.
  if (
    sameCandidate &&
    (g3.artifact_hash === current ||
      findRecordedPush(input.store.state, candidateSha, input.destination) !== undefined)
  ) {
    return { g3 };
  }
  try {
    await invalidateGateOnShaChange(input.controlRoot, "g3", current);
  } catch (err) {
    if (!(err instanceof GateRevisionConflictError)) throw err;
    // Code review L2: what was just written (an approve?) still names a G3 that
    // had to go — acting on it would re-request for an unpushed destination.
    return { g3: (await readGateFile(input.controlRoot)).g3, concurrentWrite: true };
  }
  // Code review P3 L4: the withdrawal used to be silent — the reason lived only
  // in gates.json and the old request's Mattermost post still looked approvable.
  const oldCandidate = (g3.artifact_hash.split(":")[0] ?? "").slice(0, 12);
  const why = sameCandidate
    ? `config.release đã đổi mà journal chưa có push thành công nào của candidate ` +
      `${candidateSha.slice(0, 12)} tới ${input.destination.remote}/${input.destination.target}`
    : `mission HEAD giờ là ${candidateSha.slice(0, 12)}`;
  const withdrawalNote =
    `G3 ${g3.status === "approved" ? "đã duyệt" : "đang chờ"} cho candidate ${oldCandidate} vừa bị ` +
    `rút vì ${why} — request ${g3.request_id ?? "cũ"} (kể cả bài Mattermost của nó) hết hiệu lực; ` +
    "candidate phải qua review → push → CI trước khi được xin G3 mới.";
  await input.store.commit([
    {
      event_id: randomUUID(),
      delta: {
        kind: "g3_withdrawn_for_candidate_change",
        from_candidate: g3.artifact_hash.split(":")[0] ?? "",
        to_candidate: candidateSha,
        request_id: g3.request_id ?? "",
        reason: withdrawalNote,
      },
    },
  ]);
  return { g3: (await readGateFile(input.controlRoot)).g3, withdrawalNote };
}

/** Puts the withdrawal note (L4) in front of the outcome's reason, when there is one to tell. */
export function withG3WithdrawalNote(
  outcome: ReleaseFlowResumeOutcome,
  note: string | undefined,
): ReleaseFlowResumeOutcome {
  if (note === undefined) return outcome;
  if (outcome.kind === "waiting_gate_g3")
    return {
      ...outcome,
      reason: outcome.reason === undefined ? note : `${note} ${outcome.reason}`,
    };
  if ("reason" in outcome)
    return { ...outcome, reason: `${note} ${outcome.reason}` } as ReleaseFlowResumeOutcome;
  return outcome;
}
